import type { AttackDefinition, Attributes } from '../../shared/game/combat-types.js';
import { WALK_SPEED } from '../../shared/game/combat-distances.js';
import type { ClassDefinition } from '../../shared/game/class-types.js';
import type { HeroDefinition, UnitAppearance } from '../../shared/game/world-types.js';
import { armorDefense, defendCost, type ArmorDefinition, type ShieldDefinition } from '../equipment/armor.js';
import { armorVisuals } from '../equipment/visuals.js';

export interface HeroBuild {
  id: string; name: string; color: number; spawn: { x: number; z: number };
  cls: ClassDefinition; level?: number; attributes: Attributes; armor: ArmorDefinition; shield?: ShieldDefinition; attacks: AttackDefinition[];
  /** Everything but the armor outfit, which follows from the armor worn. */
  appearance: Omit<UnitAppearance, 'archetype'>;
}
/** Level 1 character from class data. Every hero starts with the book's 6 spaces (9 m) of movement and 6 Wounds. */
export function createHero(build: HeroBuild): HeroDefinition {
  const { cls, attributes } = build;
  return {
    id: build.id, name: build.name, color: build.color, speed: WALK_SPEED, radius: 0.75, spawn: build.spawn, appearance: { ...build.appearance, archetype: armorVisuals[build.armor.id].outfit, armor: build.armor.id }, ...build.shield && { shield: build.shield.id },
    classId: cls.id, level: build.level ?? 1, armorId: build.armor.id, gear: cls.startingGear,
    // Class equipment replaces the 50 gp starting-money option (Core Rules p. 21).
    startingInventory: { gold: 0, items: {} },
    profile: { maxHp: cls.startingHp, attributes: { ...attributes }, defense: armorDefense(build.armor, attributes.dexterity, build.shield), armor: 0,
      // Armor the class is not trained in makes Defending cost an extra action.
      ...defendCost(cls.armor, build.armor, build.shield) > 1 && { defendCost: defendCost(cls.armor, build.armor, build.shield) }, maxWounds: 6, movementDistance: 9, initiative: attributes.dexterity,
      attacks: build.attacks, saves: { advantage: [...cls.saves.advantage], disadvantage: [...cls.saves.disadvantage] }, weaponProficiency: cls.weapons === 'all' ? 'all' : [...cls.weapons],
      ...(cls.weaponTypes ? { weaponTypes: [...cls.weaponTypes] } : {}) },
  };
}
